import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { serviceCategories } from '../data/servicesData';

const Services = () => {
  const servicesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observeElements = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-fade-up', 'opacity-100');
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -100px 0px',
        }
      );

      const elements = servicesRef.current?.querySelectorAll('.service-card');
      elements?.forEach((el) => {
        el.classList.add('opacity-0');
        observer.observe(el);
      });

      return observer;
    };

    const observer = observeElements();
    
    return () => {
      observer.disconnect();
    };
  }, []);

  // Select featured services - one from each category
  const featuredServices = serviceCategories.map(category => {
    const featuredService = category.services[0]; // Get the first service from each category
    return {
      id: featuredService.id,
      title: category.name,
      description: featuredService.description,
      features: featuredService.features.slice(0, 3),
      image: featuredService.image,
      categoryId: category.id
    };
  });

  return (
    <section ref={servicesRef} className="section-padding bg-pkstone-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 opacity-0 animate-fade-up">
          <span className="text-pkgreen-600 font-display uppercase tracking-wider text-sm mb-2 block">
            Our Expertise
          </span>
          <h2 className="section-title">Professional Landscape Services</h2>
          <p className="max-w-3xl mx-auto text-pkstone-700 text-lg">
            From concept to completion and ongoing care, we provide comprehensive landscape 
            services for residential and commercial properties throughout Fairfield County.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {featuredServices.map((service) => (
            <div
              key={service.id}
              className="service-card rounded-lg overflow-hidden bg-white shadow-md transition-all duration-500 ease-smooth group"
            >
              <div className="relative overflow-hidden h-60">
                <div className="absolute inset-0 bg-overlay-dark opacity-30 z-10 transition-opacity duration-300 group-hover:opacity-20"></div>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 ease-smooth group-hover:scale-105"
                  onError={(e) => {
                    e.currentTarget.src = "https://images.unsplash.com/photo-1485683761c6c5981b2b970602604d51?q=80&w=2069&auto=format&fit=crop";
                    console.error(`Image for ${service.title} failed to load`);
                  }}
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-serif font-bold mb-3 text-pkstone-900">
                  {service.title}
                </h3>
                <p className="text-pkstone-600 mb-4">
                  {service.description}
                </p>
                <ul className="mb-5 space-y-2">
                  {service.features.slice(0, 3).map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-pkgreen-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-pkstone-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/services/${service.categoryId}`}
                  className="inline-flex items-center text-pkgreen-600 font-medium hover:text-pkgreen-700 transition-colors"
                >
                  Learn more
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-16 opacity-0 animate-fade-up">
          <Link
            to="/services"
            className="bg-pkgreen-600 hover:bg-pkgreen-700 text-white font-medium py-3 px-8 rounded-md inline-flex items-center transition-all duration-300 ease-smooth"
          >
            View All Services
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Services;
